import type { ChatReelSelectionDraft, MuseDurationRange, MuseModelItem, MuseModelList, MuseModelOptionConfig } from '@/stores/chat-reel';
import type { SseReelBusiness } from '../chatCanvas/types/sseCanvasPayload';

export const AUTO_ASSIGN_MODEL_ID = 'auto_assign';

export const isImageUsageDisabled = (imageUsage: string | undefined, imageCount: number, hasVideo = false): boolean => {
  switch (imageUsage) {
    case 'first_frame_to_video':
    case 'image_to_video':
      return hasVideo || (imageCount > 0 && imageCount !== 1);
    case 'first_last_frame_to_video':
      return hasVideo || (imageCount > 0 && imageCount !== 2);
    case 'multi_reference_to_video':
      return hasVideo;
    default:
      return false;
  }
};

const EMPTY_DURATION_RANGE: MuseDurationRange = {
  min: 0,
  max: 0,
  default: 0,
  step: 1,
};

const EMPTY_MODEL_LIST: MuseModelList = {
  image_usages: {
    default: '',
    options: [],
  },
  aspect_ratios: {
    default: '',
    options: [],
  },
  resolutions: {
    default: '',
    options: [],
  },
  durations: EMPTY_DURATION_RANGE,
  audio: {
    default: false,
    options: [],
  },
  models: {
    default: {},
    groups: [],
  },
};

const normalizeOptionConfig = <T extends string | number | boolean>(config: MuseModelOptionConfig<T> | undefined, fallback: MuseModelOptionConfig<T>): MuseModelOptionConfig<T> => ({
  default: config?.default ?? fallback.default,
  options: Array.isArray(config?.options) ? config.options : fallback.options,
});

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const getPrecision = (value: number) => {
  const [, decimals = ''] = String(value).split('.');
  return decimals.length;
};

const snapDurationValue = (value: number, range: Pick<MuseDurationRange, 'min' | 'max' | 'step'>) => {
  const clamped = Math.min(range.max, Math.max(range.min, value));
  const snapped = range.min + Math.round((clamped - range.min) / range.step) * range.step;
  return Number(Math.min(range.max, Math.max(range.min, snapped)).toFixed(Math.max(getPrecision(range.min), getPrecision(range.max), getPrecision(range.step))));
};

/** 新协议使用区间对象；数组分支仅用于兼容灰度期间的旧响应。 */
export const normalizeDurationRange = (source: unknown, fallback: MuseDurationRange, preferredDefault?: number): MuseDurationRange => {
  if (Array.isArray(source)) {
    const values = source.filter(isFiniteNumber).sort((a, b) => a - b);
    if (!values.length) return fallback;

    const positiveSteps = values
      .slice(1)
      .map((value, index) => value - values[index])
      .filter((value) => value > 0);
    const step = positiveSteps.length ? Math.min(...positiveSteps) : fallback.step;
    const range = { min: values[0], max: values[values.length - 1], step: step > 0 ? step : 1 };
    const defaultValue = isFiniteNumber(preferredDefault) && values.includes(preferredDefault) ? preferredDefault : values[0];
    return { ...range, default: snapDurationValue(defaultValue, range) };
  }

  if (!source || typeof source !== 'object') return fallback;
  const candidate = source as Partial<MuseDurationRange>;
  if (!isFiniteNumber(candidate.min) || !isFiniteNumber(candidate.max)) return fallback;

  const min = Math.min(candidate.min, candidate.max);
  const max = Math.max(candidate.min, candidate.max);
  const step = isFiniteNumber(candidate.step) && candidate.step > 0 ? candidate.step : 1;
  const range = { min, max, step };
  const defaultValue = isFiniteNumber(candidate.default) ? candidate.default : isFiniteNumber(preferredDefault) ? preferredDefault : min;
  return { ...range, default: snapDurationValue(defaultValue, range) };
};

const normalizeTopLevelDurationRange = (source: unknown): MuseDurationRange => {
  if (source && typeof source === 'object' && 'options' in source) {
    const legacyConfig = source as { default?: unknown; options?: unknown };
    return normalizeDurationRange(legacyConfig.options, EMPTY_DURATION_RANGE, isFiniteNumber(legacyConfig.default) ? legacyConfig.default : undefined);
  }

  return normalizeDurationRange(source, EMPTY_DURATION_RANGE);
};

export const isDurationValueAllowed = (value: unknown, range: MuseDurationRange): value is number => {
  if (!isFiniteNumber(value) || value < range.min || value > range.max || range.step <= 0) return false;
  const steps = (value - range.min) / range.step;
  return Math.abs(steps - Math.round(steps)) < 1e-8;
};

export const resolveDurationValue = (range: MuseDurationRange, selectedValue?: number) => (isDurationValueAllowed(selectedValue, range) ? selectedValue : range.default);

export const normalizeModelList = (modelList?: Partial<MuseModelList> | null): MuseModelList => ({
  image_usages: normalizeOptionConfig(modelList?.image_usages, EMPTY_MODEL_LIST.image_usages),
  aspect_ratios: normalizeOptionConfig(modelList?.aspect_ratios, EMPTY_MODEL_LIST.aspect_ratios),
  resolutions: normalizeOptionConfig(modelList?.resolutions, EMPTY_MODEL_LIST.resolutions),
  durations: normalizeTopLevelDurationRange(modelList?.durations),
  audio: normalizeOptionConfig(modelList?.audio, EMPTY_MODEL_LIST.audio),
  models: {
    default: modelList?.models?.default ?? EMPTY_MODEL_LIST.models.default,
    groups: Array.isArray(modelList?.models?.groups)
      ? modelList.models.groups.map((group) => ({
          key: group?.key ?? '',
          label: group?.label ?? '',
          items: Array.isArray(group?.items) ? group.items : [],
        }))
      : EMPTY_MODEL_LIST.models.groups,
  },
});

export const getSelectedOption = <T extends string | number | boolean>(config: MuseModelOptionConfig<T>, selectedValue?: T) => {
  const availableOptions = config.options.filter((item) => !item.disabled);
  const value = selectedValue ?? config.default;
  return availableOptions.find((item) => item.value === value) ?? availableOptions.find((item) => item.value === config.default) ?? availableOptions[0];
};

export const getSelectedImageUsage = (config: MuseModelOptionConfig<string>, value?: string) =>
  config.options.find((item) => item.value === value && !item.disabled);

const getSupportedOptionConfig = <T extends string | number | boolean>(config: MuseModelOptionConfig<T>, supportedValues: T[] | undefined, getFallbackLabel: (value: T) => string): MuseModelOptionConfig<T> => {
  if (supportedValues === undefined) return config;

  const options = supportedValues.map((value) => ({
    label: config.options.find((item) => item.value === value)?.label ?? getFallbackLabel(value),
    value,
  }));
  return {
    default: options.some((item) => item.value === config.default) ? config.default : (options[0]?.value ?? config.default),
    options,
  };
};

export const findModelById = (modelList: MuseModelList, modelId?: string) =>
  modelList.models.groups.reduce<{ groupKey: string; item: MuseModelItem } | undefined>((result, group) => {
    if (result || !modelId) {
      return result;
    }

    const item = group.items.find((modelItem) => modelItem.id === modelId && !modelItem.disabled);
    return item ? { groupKey: group.key, item } : undefined;
  }, undefined);

export const findModelByGroupId = (modelList: MuseModelList, groupKey: string, value?: unknown) => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const valueText = String(value);
  const item = modelList.models.groups.find((group) => group.key === groupKey)?.items.find((modelItem) => modelItem.id === valueText && !modelItem.disabled);
  return item ? { groupKey, item } : undefined;
};

export const getSelectionParameterModel = (modelList: MuseModelList, draft: ChatReelSelectionDraft) => {
  const modelId = draft.modelByGroup.video || draft.modelByGroup.auto || draft.activeModelId;
  return findModelById(modelList, modelId);
};

export const getModelParameterConfigs = (modelList: MuseModelList, draft: ChatReelSelectionDraft) => {
  const parameterModel = getSelectionParameterModel(modelList, draft)?.item;
  const isAutoAssign = parameterModel?.id === AUTO_ASSIGN_MODEL_ID;
  const audioValues = !isAutoAssign && typeof parameterModel?.supported_audio === 'boolean' ? [parameterModel.supported_audio] : undefined;
  const imageUsageValues = isAutoAssign ? undefined : parameterModel?.supported_image_usage;
  const imageUsageConfig = getSupportedOptionConfig(modelList.image_usages, imageUsageValues, String);
  const topLevelDurationRange = normalizeTopLevelDurationRange(modelList.durations);
  const durationRange = !isAutoAssign && parameterModel?.supported_durations ? normalizeDurationRange(parameterModel.supported_durations, topLevelDurationRange, topLevelDurationRange.default) : topLevelDurationRange;

  return {
    imageUsageConfig: imageUsageValues === undefined ? imageUsageConfig : { ...imageUsageConfig, default: imageUsageConfig.options[0]?.value ?? imageUsageConfig.default },
    resolutionConfig: getSupportedOptionConfig(modelList.resolutions, isAutoAssign ? undefined : parameterModel?.supported_resolutions, String),
    durationRange,
    audioConfig: getSupportedOptionConfig(modelList.audio, audioValues, (value) => (value ? '开启' : '关闭')),
  };
};

const buildDefaultSelectionDraft = (modelList: MuseModelList): ChatReelSelectionDraft => {
  const defaultConfig = modelList.models.default;
  let modelDraft: ChatReelSelectionDraft = { modelByGroup: {} };

  if (defaultConfig?.model_mode === 'auto_assign') {
    const autoModel = modelList.models.groups.find((group) => group.key === 'auto')?.items.find((item) => !item.disabled);
    modelDraft = {
      modelByGroup: autoModel ? { auto: autoModel.id } : {},
      activeModelId: autoModel?.id,
    };
  }

  if (defaultConfig?.model_mode === 'manual_assign') {
    const videoModel = findModelByGroupId(modelList, 'video', defaultConfig.video_model) ?? findModelById(modelList, modelList.models.groups.find((group) => group.key === 'video')?.items.find((item) => !item.disabled)?.id);
    const imageModel = findModelByGroupId(modelList, 'image', defaultConfig.image_model) ?? findModelById(modelList, modelList.models.groups.find((group) => group.key === 'image')?.items.find((item) => !item.disabled)?.id);
    modelDraft = {
      modelByGroup: {
        ...(videoModel ? { video: videoModel.item.id } : {}),
        ...(imageModel ? { image: imageModel.item.id } : {}),
      },
      activeModelId: videoModel?.item.id || imageModel?.item.id,
    };
  }

  const { resolutionConfig, durationRange, audioConfig } = getModelParameterConfigs(modelList, modelDraft);

  return {
    ...modelDraft,
    imageUsage: undefined,
    aspectRatio: getSelectedOption(modelList.aspect_ratios)?.value,
    resolution: getSelectedOption(resolutionConfig)?.value,
    duration: resolveDurationValue(durationRange),
    audio: getSelectedOption(audioConfig)?.value,
  };
};

export const resolveReelSelectionDraft = (modelList: MuseModelList, historyDraft?: ChatReelSelectionDraft | null): ChatReelSelectionDraft => {
  const defaultDraft = buildDefaultSelectionDraft(modelList);

  if (!historyDraft || Object.keys(historyDraft).length === 0) {
    return defaultDraft;
  }

  const nextDraft = { ...defaultDraft };
  const historyModelEntries = Object.entries(historyDraft.modelByGroup || {});
  const historyModelsValid = historyModelEntries.length > 0 && historyModelEntries.every(([groupKey, modelId]) => modelList.models.groups.find((group) => group.key === groupKey)?.items.some((item) => item.id === modelId && !item.disabled));
  const historyActiveModelValid = !!historyDraft.activeModelId && Object.values(historyDraft.modelByGroup || {}).includes(historyDraft.activeModelId);

  if (historyModelsValid && historyActiveModelValid) {
    nextDraft.modelByGroup = historyDraft.modelByGroup;
    nextDraft.activeModelId = historyDraft.activeModelId;
  }

  if (modelList.aspect_ratios.options.some((item) => item.value === historyDraft.aspectRatio && !item.disabled)) {
    nextDraft.aspectRatio = historyDraft.aspectRatio;
  }
  const { imageUsageConfig, resolutionConfig, durationRange, audioConfig } = getModelParameterConfigs(modelList, nextDraft);
  nextDraft.imageUsage = getSelectedImageUsage(imageUsageConfig, historyDraft.imageUsage)?.value;
  nextDraft.resolution = getSelectedOption(resolutionConfig)?.value;
  if (resolutionConfig.options.some((item) => item.value === historyDraft.resolution && !item.disabled)) {
    nextDraft.resolution = historyDraft.resolution;
  }
  nextDraft.duration = resolveDurationValue(durationRange, historyDraft.duration);
  nextDraft.audio = getSelectedOption(audioConfig)?.value;
  if (audioConfig.options.some((item) => item.value === historyDraft.audio && !item.disabled)) {
    nextDraft.audio = historyDraft.audio;
  }

  return nextDraft;
};

export const resolveDraftVideoModelId = (modelList: MuseModelList, draft?: ChatReelSelectionDraft | null): string => {
  const resolvedDraft = resolveReelSelectionDraft(modelList, draft);
  const videoModel = findModelById(modelList, resolvedDraft.modelByGroup.video)?.item;
  if (videoModel) return videoModel.id;

  const autoModel = findModelById(modelList, resolvedDraft.modelByGroup.auto)?.item;
  return findModelByGroupId(modelList, 'video', autoModel?.video_model)?.item.id || '';
};

export const buildChatReelBusiness = (modelList: MuseModelList, draft?: ChatReelSelectionDraft | null): SseReelBusiness => {
  const resolvedDraft = resolveReelSelectionDraft(modelList, draft);
  const autoModel = findModelById(modelList, resolvedDraft.modelByGroup.auto)?.item;
  const videoModel = findModelById(modelList, resolvedDraft.modelByGroup.video)?.item;
  const imageModel = findModelById(modelList, resolvedDraft.modelByGroup.image)?.item;
  const { imageUsageConfig, resolutionConfig, durationRange, audioConfig } = getModelParameterConfigs(modelList, resolvedDraft);
  const imageUsage = getSelectedImageUsage(imageUsageConfig, resolvedDraft.imageUsage)?.value;

  return {
    model_mode: autoModel ? 'auto_assign' : 'manual_assign',
    video_model: autoModel ? String(autoModel.video_model || '') : videoModel?.id || '',
    image_model: autoModel ? String(autoModel.image_model || '') : imageModel?.id || '',
    ...(imageUsage ? { image_usage: imageUsage } : {}),
    aspect_ratio: getSelectedOption(modelList.aspect_ratios, resolvedDraft.aspectRatio)?.value || '',
    resolution: getSelectedOption(resolutionConfig, resolvedDraft.resolution)?.value || '',
    duration: resolveDurationValue(durationRange, resolvedDraft.duration),
    enable_audio: getSelectedOption(audioConfig, resolvedDraft.audio)?.value,
  };
};

export type VideoOperationCapability = 'support_video_edit' | 'support_video_extension';

export const getVideoOperationModels = (modelList: MuseModelList, capability: VideoOperationCapability) => modelList.models.groups.find((group) => group.key === 'video')?.items.filter((item) => item[capability] && !item.disabled) ?? [];

export const resolveVideoOperationSelectionDraft = (modelList: MuseModelList, capability: VideoOperationCapability, modelId: string, currentDraft?: ChatReelSelectionDraft | null): ChatReelSelectionDraft | undefined => {
  const selectedModel = findModelById(modelList, modelId);
  if (selectedModel?.groupKey !== 'video' || !selectedModel.item[capability]) return undefined;

  const resolvedCurrentDraft = resolveReelSelectionDraft(modelList, currentDraft);
  const modelByGroup: Record<string, string> = {
    ...resolvedCurrentDraft.modelByGroup,
    video: modelId,
  };
  delete modelByGroup.auto;

  return resolveReelSelectionDraft(modelList, {
    ...resolvedCurrentDraft,
    activeModelId: modelId,
    modelByGroup,
  });
};

export const buildVideoOperationReelBusiness = (modelList: MuseModelList, capability: VideoOperationCapability, draft: ChatReelSelectionDraft): SseReelBusiness | undefined => {
  const videoModel = findModelById(modelList, draft.modelByGroup.video);
  if (videoModel?.groupKey !== 'video' || !videoModel.item[capability]) return undefined;

  const video_model = videoModel.item.id;
  if (capability === 'support_video_edit') return { video_model };

  return {
    video_model,
    duration: resolveDurationValue(getModelParameterConfigs(modelList, draft).durationRange, draft.duration),
  };
};
